import { z } from 'zod';
import { type CatalogEntry, CurrencySchema, defineCatalogEntry, OwnedIdSchema } from './entry';

/**
 * Portfolio panels of the board catalog — owned by the Portfolio area. Add portfolio entries
 * HERE, never in another area's file; a duplicate id across area files is a compile error
 * (index.ts).
 *
 * Every entry must fetch its own data from simple PROPS (ids, enums) through its feature's
 * api-query hooks. The portfolio CHART children take DATA props (`analytics`), so an entry
 * wraps the TAB VIEW or card that owns the fetch and takes a `portfolioId` — never a chart.
 *
 * THE ID IS THE USER'S OWN, and the composer may only use one the main agent put in the brief
 * (ui-composition skill). Ownership is the SERVER's: every portfolio read filters by the
 * caller, so a foreign or stale id is a 404, which the FE wrapper renders as a calm
 * "not available" panel (`PortfolioPanelGate`), never a crash or an eternal spinner.
 *
 * ZOD-3 COMPATIBILITY: apps/web bundles this SOURCE with zod v3 while the backend runs v4 —
 * only syntax valid in BOTH may be used here.
 *
 * Lifecycle rules (§4.6) — an id is NEVER repurposed (persisted ops reference it forever);
 * a semantic change is a NEW entry; `retired: true` drops an entry from the composer
 * prompt while its FE binding stays so historical boards keep rendering. Props changes are
 * additive (new fields optional/defaulted); a breaking change = new id + retire the old.
 */

/**
 * `TIMEFRAME_PRESET_KEYS` values in apps/web `features/portfolio/logic/performance-chart/
 * timeframe-presets.ts`. Copied because shared-types cannot import the app; a drift fails
 * `apps/web/src/features/chatbot/boards/portfolio-catalog-parity.spec.ts`.
 */
const PORTFOLIO_TIMEFRAMES = [
  '1D',
  '1W',
  '1M',
  '3M',
  '6M',
  'YTD',
  '1Y',
  '2Y',
  '3Y',
  '5Y',
  '10Y',
  '20Y',
  'All',
] as const;

/** A placeholder id for the examples — a valid v4 uuid, never a real book. */
const EXAMPLE_PORTFOLIO_ID = '5f0c6c1e-8a51-4a4e-9d3b-2c7e1f0a9b42';

const byPortfolio = z.object({ portfolioId: OwnedIdSchema });

export const PORTFOLIO_PANELS = {
  'portfolio-performance': defineCatalogEntry({
    id: 'portfolio-performance',
    category: 'portfolio',
    description:
      'One of the user\'s portfolios over time: value vs benchmarks chart, drawdown, day/month/year return bars and top/bottom performers. Use for "how has my portfolio done" and return reviews.',
    props: z.object({
      portfolioId: OwnedIdSchema,
      timeframe: z.enum(PORTFOLIO_TIMEFRAMES).default('1Y'),
    }),
    adjustable: ['timeframe'],
    breadcrumb: ['Portfolio', 'Performance'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID, timeframe: '1Y' },
  }),
  'portfolio-allocation': defineCatalogEntry({
    id: 'portfolio-allocation',
    category: 'portfolio',
    description:
      "Where one portfolio's money sits: allocation by position, asset type, sector, geography and industry. Use for exposure, diversification and concentration questions.",
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'Allocation'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'portfolio-risk': defineCatalogEntry({
    id: 'portfolio-risk',
    category: 'portfolio',
    description:
      'The risk page of one portfolio: risk score and limits, volatility, stress tests, risk contribution, correlations and a risk-reduction plan. Use for "how risky is my portfolio".',
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'Risk analysis'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'portfolio-dividends': defineCatalogEntry({
    id: 'portfolio-dividends',
    category: 'portfolio',
    description:
      'Dividends one portfolio has RECEIVED: payouts on its value line and monthly/yearly totals, plus the income projection. Use for "how much income did my portfolio pay".',
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'Dividends'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'dividend-projection': defineCatalogEntry({
    id: 'dividend-projection',
    category: 'portfolio',
    description:
      "FUTURE dividend income of one portfolio: current annual income, yield, yield on cost and the projected 10-year income cone. Use for income planning, not past payouts. currency is an optional display currency; omit it for the portfolio's own.",
    props: z.object({ portfolioId: OwnedIdSchema, currency: CurrencySchema.optional() }),
    adjustable: [],
    breadcrumb: ['Portfolio', 'Dividends', 'Projection'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'portfolio-drawdown': defineCatalogEntry({
    id: 'portfolio-drawdown',
    category: 'portfolio',
    description:
      'The drawdown curve of one portfolio on its own: how far below its previous peak it fell, and when. Use for "what was my worst drop" and recovery questions.',
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'Performance', 'Drawdown'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'portfolio-insight': defineCatalogEntry({
    id: 'portfolio-insight',
    category: 'portfolio',
    description:
      'The AI "what moved my portfolio" card for one portfolio: a today/week/month summary and the biggest movers with reasons. Use for "why did my portfolio move".',
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'Holdings', 'AI insight'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'top-positions': defineCatalogEntry({
    id: 'top-positions',
    category: 'portfolio',
    description:
      "The user's six largest holdings across ALL portfolios with each one's share of the whole account. Takes no id. Use for account-wide concentration. currency is an optional display currency; omit it for the user's own.",
    props: z.object({ currency: CurrencySchema.optional() }),
    adjustable: [],
    breadcrumb: ['Investments', 'Top positions'],
    example: {},
  }),
  'portfolio-calendar': defineCatalogEntry({
    id: 'portfolio-calendar',
    category: 'portfolio',
    description:
      'Upcoming earnings, dividends, economic events and market holidays for the holdings of one portfolio. Use for "what is coming up for my portfolio".',
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'Calendar'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'portfolio-news': defineCatalogEntry({
    id: 'portfolio-news',
    category: 'news',
    description:
      "The news stream across one portfolio's stock and ETF holdings, largest holdings first. Use when recent events across a portfolio matter; for a single symbol use its own news panel.",
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'News'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
  'portfolio-health': defineCatalogEntry({
    id: 'portfolio-health',
    category: 'portfolio',
    description:
      'The account-wide data health check across all the user\'s portfolios, wallets and imports: a 0-100 score and the problems found (missing costs, unlinked wallets, sync errors). Takes no id; read-only. Use for "is my data right".',
    props: z.object({}),
    adjustable: [],
    breadcrumb: ['Investments', 'Health'],
    example: {},
  }),
  'portfolio-holdings': defineCatalogEntry({
    id: 'portfolio-holdings',
    category: 'portfolio',
    description:
      'The positions table of one portfolio: every holding with quantity, cost, value and gain, grouped and totalled; read-only. Use for "what do I hold" and position-level reviews.',
    props: byPortfolio,
    adjustable: [],
    breadcrumb: ['Portfolio', 'Holdings'],
    example: { portfolioId: EXAMPLE_PORTFOLIO_ID },
  }),
} as const satisfies Record<string, CatalogEntry>;
